import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, ButtonGroup, Col, Row, Table } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, helpers } from "../utils";
import { ServiceThreadSpinner, SortHeader } from "./";

const today = new Date();
const thisMonth = today.getMonth() + 1;
const thisDay = today.getDate();
const thisYear = today.getFullYear();
const startDate = `${thisMonth}/${thisDay}/${thisYear - 1}`;
const endDate = `${thisMonth}/${thisDay}/${thisYear}`;

let prevStartDateTemp = new Date(startDate);
prevStartDateTemp.setDate(prevStartDateTemp.getDate() - 1);
prevStartDateTemp.setFullYear(prevStartDateTemp.getFullYear() - 1);
const prevStartDate = prevStartDateTemp.toLocaleDateString();

let prevEndDateTemp = new Date(endDate);
prevEndDateTemp.setDate(prevEndDateTemp.getDate() - 1);
prevEndDateTemp.setFullYear(prevEndDateTemp.getFullYear() - 1);
const prevEndDate = prevEndDateTemp.toLocaleDateString();


export default function TopVendors(props) {
  const initialMount = useRef({
    firstRender: true,
    vendorClasses: "",
  });

  const [loaded, setLoaded] = useState(false);
  const [filters, setFilters] = useState({
    currentSortField: "VendorName",
    currentSortDirection: "asc"
  });
  const [stateUpdates, setStateUpdates] = useState({
    vendorClasses: [],
    vendorClassList: [],
    topVendorList: [],
    totalCurrentPurchases: 0,
    totalPreviousPurchases: 0,
    totalPercentageChange: 0,
  });

  useEffect(() => {
    document.title = `Top Vendors`
    //put the initial classes on ref
    //chain the promises so if its a first render go ahead and call the api from the result of the first api
    //then make sure the second api runs only
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();

    const fetchList = async (responseToken) => {
      await api
        .postS(
          "Purchasing/GetTopVendors",
          CreatePayload(responseToken),
          source.token
        )
        .then((response) => {
          if (response?.data?.success) {
            const data = response.data.topVendorList;
            let YTDPurchases = data.reduce(
              (totalP, current) =>
                (totalP = totalP + current.currentTotal),
              0
            );
            let prevYTD = data.reduce(
              (totalP, current) =>
                (totalP = totalP + current.previousTotal),
              0
            );
            let totalPercentageChange = helpers.percentChange(YTDPurchases, prevYTD)
            if (initialMount.current.firstRender) {
              setStateUpdates({
                ...stateUpdates,
                vendorClassList: responseToken.vendorClassList,
                topVendorList: data,
                totalCurrentPurchases: YTDPurchases,
                totalPreviousPurchases: prevYTD,
                totalPercentageChange: totalPercentageChange,
                vendorClasses: responseToken.list,
              });
              initialMount.current.firstRender = false;
            } else {
              setStateUpdates({
                ...stateUpdates,
                topVendorList: data,
                totalCurrentPurchases: YTDPurchases,
                totalPreviousPurchases: prevYTD,
                totalPercentageChange: totalPercentageChange,
                currentSortDirection: stateUpdates.currentSortDirection,
                currentSortField: stateUpdates.currentSortField,
              });
            }
          }
        })
        .catch(api.catchHandler)
        .finally(() => {
          // if this is not the first render dont skip this step
          if (initialMount.current.firstRender) return;
          setLoaded(true);
        })
    };
    //topVendorList
    const fetchVendors = async () => {
      await api
        .fetchS("Reference/GetVendorClasses", {}, source.token)
        .then((response) => {
          if (response?.data?.success) {
            let list = "";
            _.map(response.data.vendorClassList, (x) => {
              if (x.value?.charAt(0) === "1" && typeof x.value === "string") {
                list += `${x.value.trim()},`;
              }
            })
            list = list.slice(0, -1);

            return {
              list: list,
              vendorClassList: response.data.vendorClassList,
            };
          }
        })
        .then((response) => {
          fetchList(response);
        })
        .catch(api.catchHandler)
        .finally(() => {
          setLoaded(true)
        });
    };

    setLoaded(false);
    if (initialMount.current.firstRender) {
      fetchVendors();
    } else {
      fetchList();
    }

    return () => {
      source.cancel("axios request cancelled");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  function CreatePayload(responseToken) {
    return {
      StartDate: new Date(startDate),
      EndDate: new Date(endDate),
      PrevStartDate: new Date(prevStartDate),
      PrevEndDate: new Date(prevEndDate),
      TopN: 25,
      VendorClasses: initialMount.current.firstRender
        ? responseToken.list
        : getCommaDelimitedString(filters.vendorClasses),
      CurrentSortDirection: filters.currentSortDirection,
      CurrentSortField: filters.currentSortField,
    };
  }

  const exportXlsx = () => {
    api
      .postGetBlob(
        "Purchasing/ExportTopVendorsXLSX",
        CreatePayload()
      )
      .then((r) => {
        if (r.data.success === false) {
          if (_.isString(r?.data?.message)) {
            toast.error(r?.data?.message);
          }
          return;
        }
        if (r.data) {
          let XLSX_FILENAME = `TopVendors.xlsx`;
          helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_XLSX);
        } else {
          toast.error(r.message || "Error downloading report");
        }
      });
  };

  const getCommaDelimitedString = (list) => {
    if (_.isEmpty(list)) {
      // toast.error("vendor class list is falsey");
      list = stateUpdates.vendorClasses;
      return list;
    } else {
      let result = "";
      _.map(list, (x) => {
        if (typeof x.value === "string") {
          result += `${x.value.trim()},`;
        }
      });
      result = result.slice(0, -1);
      return result;
    }
  };

  const onFilterChange = (state) => {
    const Individual_State = {
      ...state
    }

    setFilters(
      ({
        ...filters,
        ...Individual_State
      })
    )
  }

  const pageSpecificSorting = (state) => {
    const { currentSortField, currentSortDirection } = state;
    const data = _.orderBy(
      stateUpdates.topVendorList,
      [currentSortField],
      [currentSortDirection]
    );


    setStateUpdates({
      ...stateUpdates,
      ...state,
      topVendorList: data,
    });
  };

  const groupStyle = {
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 'bold',
    margin: '2px 0 0 0'
  };

  const groupLabelFormat = data => (
    <div style={groupStyle}>
      <span>{data.label}</span>
    </div>
  );

  return (
    <DesktopContainer screenName={"Top 25 Vendors by Purchases Last 365 Days"}>
      <div className="filter-header">
        <Row className="my-3">
          <Col xs="3" className="text-end">
            <h5 className="pt-1 text-white">Vendor Class:</h5>
          </Col>
          <Col xs="6">
            <Select
              classNamePrefix='serviceThreadSelect'
              options={stateUpdates.vendorClassList}
              value={filters.vendorClasses}
              formatGroupLabel={groupLabelFormat}
              isMulti={true}
              onChange={(e) => onFilterChange(
                {
                  vendorClasses: e
                }
              )}
            ></Select>
          </Col>
        </Row>
        <Row>
          <Col style={{ textAlign: "center", marginRight: "5px", color: "white" }}>
            Current Purchasing Period {startDate} - {endDate}{" "}
          </Col>
        </Row>
        <Row>
          <Col style={{ textAlign: "center", marginRight: "5px", color: "white" }}>
            Previous Purchasing Period {prevStartDate} - {prevEndDate}{" "}
          </Col>
        </Row>
      </div>
      {/* <Card className="dashboard-card">
        <CardBody> */}
      <ServiceThreadSpinner on={!loaded}>
        <Row className="align-items-center my-3">
          <Col className="transparent-background">
            <ButtonGroup className="float-end">
              <Button
                title="Download as .xlsx"
                className="shredButtonExcel float-end ms-2"
                onClick={exportXlsx}
                disabled={false}
              >
                <FontAwesomeIcon icon="file-excel" size="lg" />
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
        <Table size="sm" className="data-table">
          <thead className="table-header">
            <tr>
              <th style={{ width: "35%" }}>
                <SortHeader
                  displayName={"Vendor"}
                  field={"vendorId"}
                  sortDirection={filters.currentSortDirection}
                  sorted={filters.currentSortField === "VendorId"}
                  callback={onFilterChange}
                  state={{
                    currentSortField: "VendorId",
                    currentSortDirection:
                      filters.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
              <th>
                <SortHeader
                  displayName={"Status"}
                  field={"vendorStatus"}
                  sortDirection={filters.currentSortDirection}
                  sorted={filters.currentSortField === "VendorStatus"}
                  callback={onFilterChange}
                  state={{
                    currentSortField: "VendorStatus",
                    currentSortDirection:
                      filters.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
              <th>
                <SortHeader
                  displayName={"Terms"}
                  field={"paymentTerms"}
                  sortDirection={filters.currentSortDirection}
                  sorted={filters.currentSortField === "PaymentTerms"}
                  callback={onFilterChange}
                  state={{
                    currentSortField: "PaymentTerms",
                    currentSortDirection:
                      filters.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
              <th>
                <SortHeader
                  displayName={"Class"}
                  field={"vendorClass"}
                  sortDirection={filters.currentSortDirection}
                  sorted={filters.currentSortField === "VendorClass"}
                  callback={onFilterChange}
                  state={{
                    currentSortField: "VendorClass",
                    currentSortDirection:
                      filters.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
              <th>
                <SortHeader
                  displayName={
                    <span>
                      Current
                      <br />
                      Purchases
                    </span>
                  }
                  field={"currentTotal"}
                  sortDirection={filters.currentSortDirection}
                  sorted={filters.currentSortField === "CurrentTotal"}
                  callback={onFilterChange}
                  state={{
                    currentSortField: "CurrentTotal",
                    currentSortDirection:
                      filters.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
              <th>
                <SortHeader
                  displayName={
                    <span>
                      Previous
                      <br />
                      Purchases
                    </span>
                  }
                  field={"previousTotal"}
                  sortDirection={filters.currentSortDirection}
                  sorted={filters.currentSortField === "PreviousTotal"}
                  callback={onFilterChange}
                  state={{
                    currentSortField: "PreviousTotal",
                    currentSortDirection:
                      filters.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
              <th>
                <PageSpecificSortHeader
                  displayName={
                    <span>
                      Purchase
                      <br />
                      +/-
                    </span>
                  }
                  field={"purchaseIncrease"}
                  sortDirection={stateUpdates.currentSortDirection}
                  sorted={
                    stateUpdates.currentSortField === "purchaseIncrease"
                  }
                  callback={pageSpecificSorting}
                  state={{
                    currentSortField: "purchaseIncrease",
                    currentSortDirection:
                      stateUpdates.currentSortDirection === "asc"
                        ? "desc"
                        : "asc",
                  }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {_.map(stateUpdates.topVendorList, (datum, index) => {
              let color = datum.purchaseIncrease < 0 ? "red" : "green";

              return (
                <tr
                  key={index}
                  className={`tr-${index % 2 === 0 ? "primary" : "secondary"
                    }`}
                >

                  <td children={
                    <a rel="noreferrer"
                      href={`/vendor-dashboard/${JSON.stringify({ label: datum.vendorWithName, value: datum.vendorId })}`}
                      target="_blank"
                      className="styledAnchor"
                    >
                      {datum.vendorWithName}
                    </a>
                  } />
                  <td children={datum.vendorStatus} />
                  <td children={datum.paymentTerms} />
                  <td children={datum.vendorClass} />
                  <td
                    children={helpers.formatCurrency(Math.round(datum.currentTotal), 0)}
                  />
                  <td
                    children={helpers.formatCurrency(Math.round(datum.previousTotal), 0)}
                  />
                  <td
                    style={{ color: color }}
                    children={`${Math.round(datum.purchaseIncrease)} %`}
                  />
                </tr>
              );
            })}
          </tbody>
          <tfoot className="table-footer">
            <tr>
              <td className={`fw-bold`}>TOTALS:</td>
              <td colSpan={"3"} />
              <td className={`fw-bold`}>
                {helpers.formatCurrency(Math.round(stateUpdates.totalCurrentPurchases), 0)}
              </td>
              <td className={`fw-bold`}>
                {helpers.formatCurrency(
                  Math.round(stateUpdates.totalPreviousPurchases), 0
                )}
              </td>
              <td
                className={`fw-bold`}
              >{`${stateUpdates.totalPercentageChange} %`}</td>
            </tr>
          </tfoot>
        </Table>
      </ServiceThreadSpinner>
      {/* </CardBody>
      </Card> */}
    </DesktopContainer>
  );
}

function PageSpecificSortHeader(props) {
  const [direction, setDirection] = useState(props.sortDirection || "asc");
  const callBackDir = direction === "desc" ? "asc" : "desc";

  useEffect(() => {
    setDirection(props.sortDirection || "asc");
  }, [props.sortDirection]);

  return (
    <span
      className="sort-handle"
      onClick={() =>
        props.state
          ? props.callback(props.state)
          : props.callback(props.field, callBackDir)
      }
    >
      {props.displayName}
      {props.sorted && direction === "asc" && (
        <FontAwesomeIcon icon="angle-up" className="ms-1" />
      )}
      {props.sorted && direction === "desc" && (
        <FontAwesomeIcon icon="angle-down" className="ms-1" />
      )}
    </span>
  );
}
